import { Node } from './Node';
import { Edge } from './Edge';
import { InvalidUsage } from '../exceptions/InvalidUsage';

/**
 * An UNDIRECTED graph containing nodes and edges
 * - nodes have a general purpose data member than can hold whatever data we need for a specific application
 * - edges have a general purpose data member than can hold whatever data we need for a specific application
 * data members are not intended to be accessed directly, instead use one of the getters below. They are public to allow
 * for easier JSON serialization
 */
export class Graph {
    nodes = {};
    edges = [];

    /**************** builder functions ***************/
    constructor() {}

    /**
     * build new Graph with empty nodes and edges arrays
     * @return {Graph}
     */
    static build() {
        let instance = new Graph();
        instance.nodes = {};
        instance.edges = [];
        return instance;
    }

    /**
     * create new nodes from @param names and @param datas
     * @param {string[]} names
     * @param {NodeData[]} datas
     */
    createNodes(names, datas) {
        for (let i = 0; i < names.length; i++) {
            this.addNode(names[i], datas[i]);
        }
    }

    /**
     * import @param edges and save it to this.edges
     * @param {Edge[]} edges
     */
    importEdges(edges) {
        this.edges = edges;
    }

    /**
     * add node to this.nodes
     * @param key key for nodeData
     * @param nodeData nodeData to be saved in the Node at this.nodes[key]
     */
    addNode(key, nodeData) {
        this.nodes[key] = Node.build(nodeData);
    }

    /**
     * add Edge between nodes to Graph
     * @param source edge source
     * @param destination edge destination
     * @param {EdgeData} edge_data (the EdgeData strong typing may be removed to allow the Graph to be used for other applications)
     */
    addEdge(source, destination, edgeData) {
        this.edges.push(Edge.build(source, destination, edgeData));
    }

    /**************** node getters ***************/
    /**
     * get NodeData from value at @param key
     * @param {string} key
     * @return {NodeData} nodeData
     */
    getNodeData(key) {
        if (!(key in this.nodes)) {
            throw new InvalidUsage(`Key ${key} does not exist in graph`);
        }
        return this.nodes[key].getData();
    }

    /**
     * get all NodeDatas in an array
     * @return {NodeData[]} nodeDatas
     */
    getNodeDatas() {
        let nodeDatas = {};
        for (let key in this.nodes) {
            nodeDatas[key] = this.nodes[key].getData();
        }
        return nodeDatas;
    }

    /**
     * get all Node Keys
     * @return {string[]} array of node keys
     */
    getNodeKeys() {
        return Object.keys(this.nodes);
    }

    /**
     * get number of nodes in graph
     * @return {number} number of nodes
     */
    getNodeCount() {
        return Object.keys(this.nodes).length
    }

    /**
     * get node neighbor keys around @param nodeName
     * @param {string} nodeName
     * @return {string[]} neighborKeys
     */
    getNodeNeighborKeys(nodeName) {
        let neighborKeys = [];
        for (let edge of this.edges) {
            // Assumption : no edges will be reflexive
            if (edge.getSource() === nodeName) {
                neighborKeys.push(edge.getDestination());
            }
            else if (edge.getDestination() === nodeName) {
                neighborKeys.push(edge.getSource());
            }
        }
        return neighborKeys;
    }

    /**
     * get array of NodeData from Neighbors
     * @param {string} nodeName
     * @return {NodeData[]} of NodeData
     */
    getNodeNeighborDatas(nodeName) {
        let nodeDatas = [];
        this.getNodeNeighborKeys(nodeName).forEach(key => {
            nodeDatas.push(this.getNodeData(key));
        })
        return nodeDatas;
    }

    /**************** edge getters ***************/
    /**
     * get Edge at location
     * @param {string} source
     * @param {string} destination
     * @return {Edge} edge
     */
    getEdge(source, destination) {
        let edge = this.edges.find(edge => {
            return edge.getSource() === source && edge.getDestination() === destination ||
                edge.getSource() === destination && edge.getDestination() === source;
        })
        if (edge) {
            return edge;
        }
    }

    /**
     * get Edge Data from @param source and @param destination
     * @param {string} source
     * @param {string} destination
     * @return EdgeWithData
     */
    getEdgeData(source, destination) {
        return this.getEdge(source, destination).getEdgeWithData();
    }

    /**
     * get EdgeData from Edge as EdgeData object
     * @param {string} source
     * @param {string} destination
     * @return {EdgeData} edge data
     */
    getEdgeRawData(source, destination) {
        let edge = this.getEdge(source, destination);
        return edge === null ? null : edge.getData();
    }

    /** get all edges
     * @return {Edge[]} edges
    */
    getEdges() {
        return this.edges;
    }

    /** return an array of all edges connected to a given node
     * @param {string} nodeName
     * @return array of edges surrounding
    */
    getNodeEdges(nodeName) {
        let neighbors = [];
        for (let edge of this.edges) {
            if (edge.getSource() === nodeName) {
                neighbors.push(edge);
            }
            else if (edge.getDestination() === nodeName) {
                neighbors.push(edge);
            }
        }
        return neighbors;
    }

    /** Given a set of nodes in @param array $nodeNames, @return array of edge datas whose source and destination are contained in the set of nodes nodeNames
     * @param {string[]} nodeNames
     * @return {Edge[]} edges
     */
    getComponentEdgeDatas(nodeNames) {
        let edges = [];
        for (let edge of this.edges) {
            if (nodeNames.includes(edge.getSource()) && nodeNames.includes(edge.getDestination())) {
                edges.push(edge.getEdgeWithData());
            }
        }
        return edges;
    }

    toJSON() {
        return {
            nodes: this.nodes,
            edges: this.edges
        };
    }

    static jsonDeserialize(jsonString) {
        let instance = new Graph();
        let data = jsonString;
        let nodes = data.nodes;
        let edges = data.edges;
        for (let key in nodes) {
            instance.nodes[key] = Node.jsonDeserialize(nodes[key]);
        }
        instance.edges = edges.map(edge => {
            return Edge.jsonDeserialize(edge);
        });
        return instance;
    }
}